import React, { useEffect, useState } from "react";
import swal from "sweetalert2";
import CS from "../../../services/commonService";
import BreadCrumbs from "../../common/breadcrumbs";
import SingleStatusToggle from "../../common/singleStatusToggle";
import {
	useGetMobileConfigs,
	useUpdateMobileForceUpdate,
} from "../../../queryHooks/useSettings";
import { useStore } from "../../../zustand/store";
import { validateMinVersion } from "../../../util/appVersion";

const PLATFORMS = [
	{ key: "android", label: "Android", store: "Google Play" },
	{ key: "ios", label: "iOS", store: "the App Store" },
];

//  One card per platform. Each saves on its own, so Android and iOS never affect each other.
const PlatformCard = ({ platform, config, onSave }) => {
	const [minVersion, setMinVersion] = useState("");
	const [error, setError] = useState(null);

	useEffect(() => {
		setMinVersion(config?.min_required_version || "");
		setError(null);
	}, [config?.min_required_version]);

	const enabled = Boolean(config?.force_update_enabled);

	const save = async (nextEnabled) => {
		const validation = validateMinVersion(minVersion, config);
		if (validation) {
			setError(validation);
			return;
		}
		setError(null);

		if (nextEnabled) {
			const result = await swal.fire({
				title: `Force update ${platform.label}?`,
				text: `${platform.label} users on a version below ${minVersion} will be blocked until they update from ${platform.store}. Make sure that version is already live in the store.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#3085d6",
				cancelButtonColor: "#d33",
				confirmButtonText: "Yes, force update",
			});
			if (!result.isConfirmed) return;
		}

		onSave({
			platform: platform.key,
			enabled: nextEnabled,
			min_required_version: minVersion,
		});
	};

	return (
		<div className="col-md-6 mb-4">
			<div className="card-strip h-100">
				<div className="modal-header p-0 pb-3 mb-4 w-100 d-flex align-items-center justify-content-between">
					<h5 className="modal-title">{platform.label}</h5>
					<div className="d-flex align-items-center">
						<span className="mr-2">Force Update {enabled ? "On" : "Off"}</span>
						<SingleStatusToggle
							_id={`force-update-${platform.key}`}
							htmlFor={`force-update-${platform.key}`}
							status={enabled}
							disabled={!config}
							onToggleChange={() => save(!enabled)}
						/>
					</div>
				</div>

				<div className="row mb-3">
					<div className="col-6">
						<label>Latest version</label>
						<p className="mb-0 font-weight-bold">
							{config?.latest_version || "-"}
						</p>
					</div>
					<div className="col-6">
						<label>Recommended version</label>
						<p className="mb-0 font-weight-bold">
							{config?.min_recommended_version || "-"}
						</p>
					</div>
				</div>

				<div className="form-group mb-2">
					<label htmlFor={`min-version-${platform.key}`}>
						Minimum required version
					</label>
					<input
						type="text"
						className="form-control"
						id={`min-version-${platform.key}`}
						placeholder="e.g. 1.0.18"
						value={minVersion}
						onChange={(e) => setMinVersion(e.target.value.trim())}
					/>
					<small className="text-muted d-block mt-1">
						While Force Update is on, {platform.label} users below this version
						must update before using the app.
					</small>
					<span className="text-danger d-block mt-1">{error}</span>
				</div>

				<div className="text-right">
					<button
						type="button"
						className="btn btn-primary glow-primary"
						disabled={!config}
						onClick={() => save(enabled)}
					>
						Save {platform.label}
					</button>
				</div>
			</div>
		</div>
	);
};

//  Component starts
const AppVersion = () => {
	const setLoading = useStore((state) => state.setLoading);
	const { data, refetch, isLoading } = useGetMobileConfigs();
	const { mutateAsync: updateForceUpdate } = useUpdateMobileForceUpdate();

	const configs = data?.data;

	const handleSave = async (body) => {
		setLoading(true);
		try {
			const response = await updateForceUpdate(body);
			if (response?.status === "success") {
				CS.showSuccessMessage(response.message);
				await refetch();
			} else {
				CS.showErrorMessage(response?.message);
			}
		} catch (error) {
			CS.showErrorMessage(
				error?.response?.data?.message || "Could not save. Please try again."
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<BreadCrumbs
				title="Settings"
				data={[{ label: "App Version & Force Update" }]}
			/>
			<div className="card">
				<div className="card mb-5 bg-transparent">
					<p className="mb-4">
						Turn on Force Update for a platform to block users on that platform
						whose app is older than the minimum required version, until they
						install the update. Android and iOS are controlled separately.
					</p>
					{isLoading ? (
						<p>Loading…</p>
					) : (
						<div className="row">
							{PLATFORMS.map((platform) => (
								<PlatformCard
									key={platform.key}
									platform={platform}
									config={configs?.[platform.key]}
									onSave={handleSave}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default AppVersion;
